// Settings ("Настройки") — ports profile.js: subscription card, settings
// toggles (emotion recognition, push), password change, cancel subscription,
// user-id copy, support links. Profile/stats/settings load on mount.

import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useAuthStore } from '../../store/authStore'
import { useProfileStore } from '../../store/profileStore'
import { profileApi } from '../../api/profile'
import type { ProfileSettings } from '../../api/profile'
import { messageFromError } from '../../api/client'
import { paymentsApi } from '../../api/payments'
import { engineStyleApi, type EngineStyleState } from '../../api/chat'
import { acquirePaymentAppResume, getPaymentChannel } from '../../payments/channel'
import { useAccountSegment } from '../../features/product-segment'
import { usePushNotifications } from '../../hooks/usePushNotifications'
import { useInstallStore } from '../../store/installStore'
import { isStandalonePwa } from '../../lib/install'
import { isNative } from '../../lib/platform'
import { toast } from '../../store/toastStore'
import SubscriptionCard from './SubscriptionCard'
import PasswordModal from './PasswordModal'
import NameModal from './NameModal'
import Toggle from '../../components/Toggle'
import styles from './Settings.module.css'

// Adaptive install card — ports profile.js:435-480 platform detection: hide when
// already installed, offer a native install button when the browser supports it
// (beforeinstallprompt), fall back to iOS "Share → На экран Домой" steps.
//
// Exported for the unit test: rendering the whole Settings screen would drag in
// the profile/payments/push stack for what is a self-contained card.
export function InstallCard() {
  const deferredPrompt = useInstallStore((s) => s.deferredPrompt)
  const clearPrompt = useInstallStore((s) => s.clear)

  // Native (Capacitor): the app IS the installation. `beforeinstallprompt` never
  // fires in the WebView and isStandalonePwa() is false there (display-mode is
  // not 'standalone'), so without this guard the card falls through to its
  // browser-instructions branch and tells an installed-app user to open a
  // browser menu. Mirrors the same guard in components/InstallPrompt.tsx.
  if (isNative) return null

  const standalone = isStandalonePwa()
  const isIOS = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent)

  if (standalone) {
    return (
      <div className={styles.installCard}>
        <h3>Приложение установлено ✓</h3>
        <p className={styles.installText}>Humanteq уже добавлен на ваше устройство.</p>
      </div>
    )
  }

  const install = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    try {
      await deferredPrompt.userChoice
    } catch {
      /* ignore */
    }
    clearPrompt()
  }

  let body
  if (deferredPrompt) {
    body = <button className={styles.installBtn} onClick={install}>Установить приложение</button>
  } else if (isIOS) {
    body = (
      <ol className={styles.installSteps}>
        <li>Нажмите кнопку «Поделиться» в браузере</li>
        <li>Выберите «На экран „Домой"»</li>
        <li>Нажмите «Добавить»</li>
      </ol>
    )
  } else {
    body = <p className={styles.installText}>Откройте меню браузера и выберите «Установить приложение».</p>
  }

  return (
    <div className={styles.installCard}>
      <h3>Добавить на рабочий стол</h3>
      <p className={styles.installText}>Используйте Humanteq как полноценное приложение</p>
      {body}
    </div>
  )
}

export default function Settings() {
  const userId = useAuthStore((s) => s.userId)
  const displayName = useAuthStore((s) => s.displayName)
  const setDisplayName = useAuthStore((s) => s.setDisplayName)
  const stats = useProfileStore((s) => s.stats)
  const loadStats = useProfileStore((s) => s.loadStats)
  const paymentChannel = getPaymentChannel()
  const { profile } = useAccountSegment()
  // break_up (#4480, 17.10): the Telegram support channel belongs to the main
  // build; the breakup app supports via in-app chat, VK and email.
  // break_up (#4487): its funnel is passwordless (email code only), so the
  // password-change row has nothing to change there.
  // #4488: the segment arrives with the profile (async) — render both rows
  // only once it is known, otherwise they flash on breakup for a second
  // before the profile lands and removes them.
  const mainBuildRows = profile !== undefined && profile.segment !== 'break_up'
  const showTelegramRow = mainBuildRows
  const showPasswordRow = mainBuildRows

  const {
    enable: enablePush,
    disable: disablePush,
    refresh: refreshPush,
    isEnabled: isPushEnabled,
  } = usePushNotifications()
  const [emotion, setEmotion] = useState(true)
  const [styleState, setStyleState] = useState<EngineStyleState | null>(null)
  const [notifications, setNotifications] = useState(false)
  const [pwdOpen, setPwdOpen] = useState(false)
  const [nameOpen, setNameOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [paymentAction, setPaymentAction] = useState<'cancel' | 'manage' | null>(null)
  const [paymentError, setPaymentError] = useState('')
  const paymentActionRef = useRef(false)

  useEffect(() => {
    let active = true
    loadStats(true)
    profileApi
      .getSettings()
      .then((s) => {
        if (active) setEmotion(s.emotion_recognition !== false)
      })
      .catch(() => {})
    engineStyleApi
      .get()
      .then((s) => {
        if (active) setStyleState(s)
      })
      .catch(() => {}) // engine off / legacy — the row stays hidden
    refreshPush()
      .then(() => isPushEnabled())
      .then((enabled) => {
        if (active) setNotifications(enabled)
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [isPushEnabled, loadStats, refreshPush])

  useEffect(() => {
    if (paymentChannel.provider !== 'rustore') return
    let release: (() => Promise<void>) | undefined
    let disposed = false
    void acquirePaymentAppResume(paymentChannel)
      .then((acquiredRelease) => {
        if (disposed) void acquiredRelease()
        else release = acquiredRelease
      })
      .catch(() => {})
    return () => {
      disposed = true
      if (release) void release()
    }
  }, [paymentChannel])

  const saveSetting = async (patch: ProfileSettings) => {
    try {
      await profileApi.saveSettings(patch)
    } catch {
      toast('Не удалось сохранить настройки', 'error')
    }
  }

  const onEmotion = (e: ChangeEvent<HTMLInputElement>) => {
    setEmotion(e.target.checked)
    saveSetting({ emotion_recognition: e.target.checked })
  }

  // This toggle reflects the current browser/app installation. The backend's
  // notifications_enabled flag is account-level and may remain true while a
  // different device is still subscribed.
  const onNotifications = async (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setNotifications(checked)
    if (checked) {
      const ok = await enablePush()
      setNotifications(ok) // permission denied / failed → revert the toggle
    } else {
      const ok = await disablePush()
      setNotifications(!ok) // failed → keep the current device shown as enabled
    }
  }

  // §2.1.3: style change is effective from the next reply (block 1
  // re-reads preferences every turn); a mid-session change journals the
  // §3.3 line onto the last bot row server-side
  const onStyleSelect = async (kind: string) => {
    if (!styleState || kind === styleState.style) return
    const prev = styleState
    setStyleState({ ...styleState, style: kind })
    try {
      const fresh = await engineStyleApi.set(kind)
      setStyleState(fresh)
      toast('Стиль общения обновлён', 'success')
    } catch (err) {
      setStyleState(prev) // failed → keep the shown truth
      toast(messageFromError(err, 'Не удалось изменить стиль'), 'error')
    }
  }

  const beginPaymentAction = (action: 'cancel' | 'manage') => {
    if (paymentActionRef.current) return false
    paymentActionRef.current = true
    setPaymentAction(action)
    setPaymentError('')
    return true
  }

  const finishPaymentAction = () => {
    paymentActionRef.current = false
    setPaymentAction(null)
  }

  const cancelSubscription = async () => {
    if (!beginPaymentAction('cancel')) return
    try {
      const data = await paymentsApi.cancel()
      const until = data.active_until ? new Date(data.active_until).toLocaleDateString() : ''
      toast(`Подписка отменена. Доступ сохранён до ${until}`, 'success')
      await loadStats(true)
    } catch (err) {
      toast(messageFromError(err, 'Не удалось отменить подписку'), 'error')
    } finally {
      finishPaymentAction()
    }
  }

  const manageSubscription = async () => {
    if (!beginPaymentAction('manage')) return
    try {
      await paymentChannel.manageSubscription()
    } catch {
      setPaymentError('Не удалось открыть управление подпиской. Попробуйте позже')
    } finally {
      finishPaymentAction()
    }
  }

  const copyUserId = () => {
    if (!userId) return
    navigator.clipboard.writeText(userId).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    })
  }

  return (
    <div className={styles.wrap}>
      <header className={styles.profileHeader}>
        <div className={styles.avatar}>{(displayName || 'U')[0].toUpperCase()}</div>
        <h2>{displayName || 'User'}</h2>
      </header>

      <SubscriptionCard
        stats={stats}
        onCancel={cancelSubscription}
        onManage={manageSubscription}
        canManage={isNative && paymentChannel.provider === 'rustore' && stats?.subscription_provider === 'rustore'}
        actionPending={paymentAction !== null}
      />
      {paymentError && <p role="alert" className={styles.muted}>{paymentError}</p>}

      <div className={styles.group}>
        <h3 className={styles.groupTitle}>Настройки</h3>
        <div className={styles.row}>
          <img className={styles.rowIcon} src="/assets/bell.svg" alt="" />
          <span className={styles.rowLabel}>Push-уведомления</span>
          <Toggle checked={notifications} onChange={onNotifications} />
        </div>
        <div className={styles.row}>
          <img className={styles.rowIcon} src="/assets/clock.svg" alt="" />
          <span className={styles.rowLabel}>Распознавание эмоций</span>
          <Toggle checked={emotion} onChange={onEmotion} />
        </div>
        {styleState?.enabled !== false && styleState && (
          <div className={styles.styleGroup}>
            <div className={styles.row}>
              <img className={styles.rowIcon} src="/assets/user.svg" alt="" />
              <span className={styles.rowLabel}>Стиль общения</span>
            </div>
            <div className={styles.styleOptions}>
              {(['soft', 'analytic', 'challenge'] as const).map((kind) => (
                <button
                  key={kind}
                  className={`${styles.styleOption} ${styleState.style === kind ? styles.styleOptionSelected : ''}`}
                  onClick={() => onStyleSelect(kind)}
                >
                  <span className={styles.styleOptionLabel}>
                    {styleState.labels?.[kind]?.label ?? kind}
                  </span>
                  <span className={styles.styleOptionHint}>
                    {styleState.labels?.[kind]?.hint ?? ''}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.group}>
        <h3 className={styles.groupTitle}>Основное</h3>
        <button className={styles.row} onClick={() => setNameOpen(true)}>
          <img className={styles.rowIcon} src="/assets/user.svg" alt="" />
          <span className={styles.rowLabel}>Имя: {displayName || '—'}</span>
          <span className={styles.chevron}>›</span>
        </button>
        {showPasswordRow && (
          <button className={styles.row} onClick={() => setPwdOpen(true)}>
            <img className={styles.rowIcon} src="/assets/lock.svg" alt="" />
            <span className={styles.rowLabel}>Сменить пароль</span>
            <span className={styles.chevron}>›</span>
          </button>
        )}
      </div>

      <InstallCard />

      <div className={styles.group}>
        <h3 className={styles.groupTitle}>Написать в поддержку</h3>
        <button className={styles.row} onClick={copyUserId}>
          <img className={styles.rowIcon} src="/assets/user.svg" alt="" />
          <span className={styles.rowLabel}>ID: {userId || ''}</span>
          <span className={styles.chevron}>{copied ? '✓' : '›'}</span>
        </button>
        <a className={styles.row} href="https://vk.com/humanteq" target="_blank" rel="noopener noreferrer">
          <img className={styles.rowIcon} src="/assets/icon-vk.svg" alt="" />
          <span className={styles.rowLabel}>ВКонтакте</span>
          <span className={styles.chevron}>›</span>
        </a>
        {showTelegramRow && (
          <a className={styles.row} href="https://t.me/humanteq_support" target="_blank" rel="noopener noreferrer">
            <img className={styles.rowIcon} src="/assets/icon-tg.svg" alt="" />
            <span className={styles.rowLabel}>Telegram</span>
            <span className={styles.chevron}>›</span>
          </a>
        )}
        <a className={styles.row} href="mailto:letsconnect@humanteq.io">
          <img className={styles.rowIcon} src="/assets/icon-mail.svg" alt="" />
          <span className={styles.rowLabel}>Почта</span>
          <span className={styles.chevron}>›</span>
        </a>
      </div>

      {pwdOpen && <PasswordModal onClose={() => setPwdOpen(false)} />}
      {nameOpen && (
        <NameModal
          current={displayName || ''}
          onClose={() => setNameOpen(false)}
          onSaved={setDisplayName}
        />
      )}

      <p className={styles.commitMeta}>v{__APP_VERSION__} · {__COMMIT_HASH__} · {__BUILD_DATE__}</p>
    </div>
  )
}
